import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3,
  FolderKanban,
  Gavel,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MessageSquareText,
  Moon,
  Plus,
  ScrollText,
  Settings,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { APP_ROLE_META } from '@ssn/shared';
import { useSession } from '@/session/SessionProvider';
import { useLogout } from '@/lib/queries';
import { currentTheme, toggleTheme, type ThemeChoice } from '@/lib/theme';
import { Avatar } from '@/components/ui/Avatar';
import { Button, IconButton, LinkButton } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { GROUP_LABELS, NAVIGATION, type NavItem } from './navigation';

const ICONS: Record<string, ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  LayoutDashboard,
  ListChecks,
  FolderKanban,
  MessageSquareText,
  Gavel,
  Users,
  BarChart3,
  ScrollText,
  Settings,
};

export function AppShell() {
  const { session, can, canAny } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Navigating on a phone should close the drawer, or the new page is hidden
  // behind the menu that was used to reach it.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const items = NAVIGATION.filter((item) =>
    typeof item.permission === 'string' ? can(item.permission) : canAny(...item.permission),
  );
  const groups = (['work', 'oversight', 'admin'] as const)
    .map((group) => ({ group, items: items.filter((item) => item.group === group) }))
    .filter((entry) => entry.items.length > 0);

  return (
    <div className="min-h-dvh bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:shadow-overlay"
      >
        Skip to content
      </a>

      <div className="flex">
        <Sidebar groups={groups} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenMenu={() => setMobileOpen(true)} />

          <main id="main" className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-[1400px]">
              <Outlet />
            </div>
          </main>

          <footer className="border-t border-border px-4 py-3 text-[11px] text-text-subtle sm:px-6 lg:px-8">
            Spring Side Network — Development Hub
            {session && (
              <>
                {' · '}signed in as {session.user.displayName} ({APP_ROLE_META[session.user.appRole].shortLabel})
              </>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}

function Sidebar({
  groups,
  mobileOpen,
  onClose,
}: {
  groups: readonly { group: NavItem['group']; items: readonly NavItem[] }[];
  mobileOpen: boolean;
  onClose: () => void;
}) {
  const { can } = useSession();

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}

      <nav
        aria-label="Main"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-border bg-surface',
          'transition-transform duration-200 lg:sticky lg:top-0 lg:h-dvh lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text">Development Hub</p>
            <p className="truncate text-[11px] text-text-subtle">Spring Side Network</p>
          </div>
          <IconButton label="Close menu" className="lg:hidden" onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        </div>

        {can('task:create') && (
          <div className="px-3 pt-3">
            <LinkButton
              to="/tasks/new"
              variant="primary"
              className="w-full"
              icon={<Plus aria-hidden className="size-4" />}
            >
              Create Task
            </LinkButton>
          </div>
        )}

        <div className="scrollbar-thin flex-1 overflow-y-auto px-3 py-3">
          {groups.map(({ group, items }) => (
            <div key={group} className="mb-4 last:mb-0">
              <p className="px-2 pb-1.5 text-[10px] font-semibold tracking-wider text-text-subtle uppercase">
                {GROUP_LABELS[group]}
              </p>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = ICONS[item.icon] ?? LayoutDashboard;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={!item.matchPrefix}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'bg-accent-soft font-medium text-accent-text'
                              : 'text-text-muted hover:bg-surface-hover hover:text-text',
                          )
                        }
                      >
                        <Icon aria-hidden className="size-4 shrink-0" />
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}

function TopBar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const { session } = useSession();
  const logout = useLogout();
  const [theme, setThemeState] = useState<ThemeChoice>(() => currentTheme());

  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-surface/85 px-4 py-2.5 backdrop-blur sm:px-6 lg:px-8">
      <IconButton label="Open menu" className="lg:hidden" onClick={onOpenMenu}>
        <Menu aria-hidden className="size-4" />
      </IconButton>

      <div className="ml-auto flex items-center gap-1.5">
        <IconButton
          label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          onClick={() => setThemeState(toggleTheme())}
        >
          {theme === 'dark' ? <Sun aria-hidden className="size-4" /> : <Moon aria-hidden className="size-4" />}
        </IconButton>

        {session && (
          <div className="flex items-center gap-2 border-l border-border pl-2.5">
            <Avatar user={session.user} size="md" />
            <div className="hidden min-w-0 sm:block">
              <p className="truncate text-xs font-medium text-text">{session.user.displayName}</p>
              <p className="truncate text-[11px] text-text-subtle">
                {APP_ROLE_META[session.user.appRole].shortLabel}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              loading={logout.isPending}
              onClick={() => {
                logout.mutate(undefined, {
                  onSuccess: () => {
                    window.location.href = '/signin';
                  },
                });
              }}
              icon={<LogOut aria-hidden className="size-4" />}
            >
              <span className="sr-only sm:not-sr-only">Sign out</span>
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
